<template>
    <div class="department-user-row" :class="{ 'blur-row': !isDepartmentActive }">
        <div class="target-cell">
            <div class="target-info">
                <div class="indent"></div>
                <i class="material-icons user-icon">person</i>
                <span class="target-name">{{ user.name }}</span>
                <span v-if="hasUserOverride" class="override-badge">
                    <i class="material-icons">verified_user</i>
                    {{ $t('table.specialPermission') }}
                </span>
            </div>
        </div>

        <div 
            v-for="group in scopeGroups" 
            :key="group.id" 
            class="scope-group-cell"
        >
            <div class="scope-toggles">
                <div 
                    v-for="scope in group.scopes" 
                    :key="scope"
                    class="scope-toggle-cell"
                    :class="{ 
                        'from-department': hasScopeFromDepartment(scope) && !hasUserOverride && isDepartmentActive,
                        'from-user': hasScopeFromUser(scope),
                        'user-override-active': hasUserOverride
                    }"
                >
                    <div class="scope-indicator">
                        <i class="material-icons" v-if="hasMergedScope(scope)">done</i>
                        <i class="material-icons" v-else>circle</i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "DepartmentUserRow",
    props: {
        user: {
            type: Object,
            required: true,
        },
        departmentScopes: {
            type: Array,
            default: () => [],
        },
        userTarget: {
            type: Object,
            default: null,
        },
        scopeGroups: {
            type: Array,
            required: true,
        },
        isDepartmentActive: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        hasUserOverride() {
            return !!this.userTarget;
        },
    },
    methods: {
        hasScopeFromDepartment(scope) {
            // Kullanıcıya özel yetki varsa departman IGNORE edilir
            if (this.hasUserOverride) return false;
            
            // Departman aktif değilse departman scope'ları gözükmesin
            if (!this.isDepartmentActive) return false;
            
            return this.departmentScopes.includes(scope);
        },
        hasScopeFromUser(scope) {
            if (!this.userTarget) return false;
            return (this.userTarget.scopes || []).includes(scope);
        },
        hasMergedScope(scope) {
            // Kullanıcıya özel yetki varsa SADECE onu kontrol et (departman ignore)
            if (this.hasUserOverride) {
                return this.hasScopeFromUser(scope);
            }
            
            // Kullanıcıya özel yetki yoksa departman yetkisini göster
            if (this.isDepartmentActive) {
                return this.hasScopeFromDepartment(scope);
            }
            
            return false;
        },
    },
});
</script>

<style scoped lang="scss">
.department-user-row {
    display: flex;
    border-bottom: 1px solid var(--color-border-subdued);
    min-height: 45px;
    background: #fdfdfd;

    &.blur-row {
        opacity: 0.5;
        filter: blur(0.5px);
    }

    &:hover {
        background: #f5f5f5;

        &.blur-row {
            opacity: 0.8;
            filter: blur(0);
        }
    }

    .target-cell {
        width: 250px;
        min-width: 250px;
        flex-shrink: 0;
        padding: 6px 12px;
        display: flex;
        align-items: center;
        border-right: 1px solid var(--color-border-default);

        .target-info {
            display: flex;
            align-items: center;
            gap: 6px;
            flex: 1;
            min-width: 0;

            .indent {
                width: 20px;
                flex-shrink: 0;
            }

            .user-icon {
                font-size: 16px;
                color: var(--color-icon-subdued);
                flex-shrink: 0;
            }

            .target-name {
                font-weight: 400;
                font-size: 13px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .override-badge {
                display: flex;
                align-items: center;
                gap: 3px;
                font-size: 11px;
                color: #1890ff;
                background: #e6f7ff;
                padding: 2px 6px;
                border-radius: 10px;
                white-space: nowrap;

                .material-icons {
                    font-size: 14px;
                }
            }
        }
    }

    .scope-group-cell {
        flex: 1;
        display: flex;
        border-right: 1px solid var(--color-border-default);

        &:last-child {
            border-right: none;
        }

        .scope-toggles {
            display: flex;
            width: 100%;

            .scope-toggle-cell {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                border-right: 1px solid var(--color-border-subdued);
                padding: 0;

                &:last-child {
                    border-right: none;
                }

                &.from-department {
                    background: rgba(24, 144, 255, 0.05);
                }

                &.from-user {
                    background: rgba(82, 196, 26, 0.08);
                }

                .scope-indicator {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    min-height: 45px;

                    .material-icons {
                        font-size: 18px;
                        
                        &:first-child {
                            color: var(--color-icon-success);
                        }

                        &:last-child {
                            color: var(--color-icon-subdued);
                            opacity: 0.3;
                        }
                    }
                }
            }
        }
    }
}
</style>

